import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { sendNotesToAI } from '../api/aiNotesToContent';
import useAIStickyGeneration from '../hooks/useAIStickyGeneration';
import { updateNote } from '../api/notesApi';
import { rewriteTextWithAI } from '../api/aiRewrite';
import { useStickies } from './contexts/StickiesContext';
import { useNotes } from './contexts/NotesContext';
import './AICompositionModal.css';
import { sendStickiesToAIComposition } from '../api/aiComposition';

const AICompositionModal = ({ 
  isOpen, 
  onClose, 
  stickies = [], 
  selectedStickyIds = []
}) => {
  // Position and size state for floating window
const [position, setPosition] = useState({ 
   x: 650, 
  y: 350 
});
  const [size, setSize] = useState({ width: 900, height: 700 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });

  // Original component state
  const [selectedStickies, setSelectedStickies] = useState([]);
  const [aiOutput, setAiOutput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showAIResponse, setShowAIResponse] = useState(false);
  const [customTask, setCustomTask] = useState("Create a single, cohesive passage that combines elements from all selected notes.");
  const { generateAndCreateStickies, isGenerating: isGeneratingFromHook } = useAIStickyGeneration();
  const [promptForGeneration, setPromptForGeneration] = useState("");
  const [stickyCount, setStickyCount] = useState(3);
  const [isGeneratingStickies, setIsGeneratingStickies] = useState(false);
  
  // Context menu state and refs
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
  const [selectedText, setSelectedText] = useState("");
  const [isRewriting, setIsRewriting] = useState(false);
  const outputBoxRef = useRef(null);
  
  // Context for creating subnotes
  const { effectiveNoteId, isSubnote } = useStickies();
  const [isCreatingSubnote, setIsCreatingSubnote] = useState(false);
  const { createSubnote, updateNoteContent, refreshNotes, selectNote } = useNotes();

  // Dragging handlers
  const handleMouseDown = (e) => {
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y
    };
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStartRef.current.x,
      y: e.clientY - dragStartRef.current.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Resize handlers
  const handleResizeStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    
    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = size.width;
    const startHeight = size.height;

    const handleMouseMove = (moveEvent) => {
      setSize({
        width: Math.max(600, startWidth + (moveEvent.clientX - startX)),
        height: Math.max(400, startHeight + (moveEvent.clientY - startY))
      });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Set up drag event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging]);

  // Original useEffect hooks
  useEffect(() => {
    if (stickies && selectedStickyIds) {
      const selectedIdsAsStrings = selectedStickyIds.map(id => String(id));
      const filtered = stickies.filter(sticky => selectedIdsAsStrings.includes(String(sticky.id)));
      setSelectedStickies(filtered);
    }
  }, [stickies, selectedStickyIds]);

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showContextMenu && !event.target.closest('.context-menu')) {
        setShowContextMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showContextMenu]);

  // Original handler functions (unchanged)
  const handleSendToAI = async () => {
    if (!selectedStickies.length) {
      console.warn("No stickies selected for AI composition.");
      return;
    }

    setIsLoading(true);

    try {
     const aiResponse = await sendStickiesToAIComposition(
        selectedStickies, 
        "compose_note", 
        { note_id: 1, note_title: "AI Composition" }
      );

      if (aiResponse) {
        // Convert structured JSON to displayable text
        const formattedOutput = formatStructuredResponse(aiResponse);
        setAiOutput(formattedOutput);
        setShowAIResponse(true);
      }
    } catch (error) {
      console.error("Failed to process AI request:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatStructuredResponse = (response) => {
  let formatted = `# ${response.title}\n\n`;
  
  if (response.summary?.length) {
    formatted += `## Summary\n`;
    response.summary.forEach(item => formatted += `• ${item}\n`);
    formatted += `\n`;
  }
  
  if (response.claims?.length) {
    formatted += `## Key Claims\n`;
    response.claims.forEach(claim => {
      formatted += `• ${claim.claim}`;
      if (claim.evidence?.length) {
        formatted += ` (Evidence: ${claim.evidence.join(', ')})`;
      }
      formatted += `\n`;
    });
    formatted += `\n`;
  }
  
  if (response.risks?.length) {
    formatted += `## Risks & Unknowns\n`;
    response.risks.forEach(risk => formatted += `• ${risk}\n`);
    formatted += `\n`;
  }
  
  if (response.next_actions?.length) {
    formatted += `## Next Actions\n`;
    response.next_actions.forEach(action => {
      formatted += `• ${action.task} (Owner: ${action.owner}, Effort: ${action.effort})\n`;
    });
  }
  
  return formatted;
};

  const handleCreateSubnote = async () => {
    if (!aiOutput || !effectiveNoteId) return;
    
    setIsCreatingSubnote(true);
    console.log("Creating subnote with AI content length:", aiOutput.length);
    
    try {
      const currentUser = JSON.parse(localStorage.getItem('user'));
      const userId = currentUser?.user_id;
      
      if (!userId) {
        throw new Error("User information not available");
      }
      
      console.log(`Creating subnote under parent: ${effectiveNoteId}`);
      const newSubnote = await createSubnote(effectiveNoteId, userId);
      
      if (newSubnote && newSubnote.id) {
        console.log(`Subnote created with ID: ${newSubnote.id}`);
        
        try {
          const paragraphs = aiOutput.split('\n\n');
          const formattedContent = '<p>' + paragraphs.join('</p><p>&nbsp;</p><p>') + '</p>';
          const finalContent = formattedContent
            .replace(/<p><\/p>/g, '')
            .replace(/<p>&nbsp;<\/p>$/, '');
          
          console.log(`Updating subnote ${newSubnote.id} with AI content`);
          await updateNote(newSubnote.id, {
            subId: newSubnote.subId,
            noteType: newSubnote.noteType,
            title: "AI Generated Content",
            note: finalContent,
            tags: newSubnote.tags || "",
            primary_category_id: newSubnote.primary_category_id,
            secondary_categories: newSubnote.secondary_categories || "[]",
            parent_id: newSubnote.parent_id
          });
          
          onClose();
          await refreshNotes();
          selectNote(newSubnote.id);
        } catch (updateError) {
          console.error("Created subnote but failed to update content:", updateError);
        }
      }
    } catch (error) {
      console.error("Failed to create subnote:", error);
      alert("Failed to create subnote. Please try again.");
    } finally {
      setIsCreatingSubnote(false);
    }
  };

  // Add this handler function after the existing handleCreateSubnote function

const handleGenerateStickies = async () => {
  if (!promptForGeneration.trim()) {
    console.warn("No prompt provided for sticky generation.");
    return;
  }

  try {
    console.log("Generating stickies:", {
      prompt: promptForGeneration,
      count: stickyCount
    });
    
    const result = await generateAndCreateStickies(promptForGeneration, stickyCount);
    
    if (result.success) {
      console.log(`✅ Successfully created ${result.count} stickies`);
      setPromptForGeneration(""); // Clear prompt on success
      
      // Optional: Show success message
      if (result.tokenUsage) {
        console.log(`Token usage: ${result.tokenUsage}`);
      }
    } else {
      console.error("Failed to generate stickies:", result.message);
      alert(result.message);
    }
    
  } catch (error) {
    console.error("Failed to generate stickies:", error);
    alert("Failed to generate stickies. Please try again.");
  }
};

  const formatStickyForDisplay = (sticky, index) => {
    const content = sticky.content || '';
    const displayContent = content.length > 100 ? content.substring(0, 100) + '...' : content;

    const getTypeIcon = (type) => {
      switch(type) {
        case 'ai': return '🤖';
        case 'checklist': return '☑️';
        default: return '📝';
      }
    };

    return (
      <div key={sticky.id} className="ai-composition-sticky">
        <div className="ai-composition-sticky-header">
          <span className="ai-composition-sticky-number">#{index + 1}</span>
         
        </div>
        <div className="ai-composition-sticky-content">
          {displayContent}
        </div>
      </div>
    );
  };
const handleContextMenu = (e) => {
  e.preventDefault();
  const selection = window.getSelection();
  const selectedText = selection.toString().trim();
  
  if (selectedText) {
    setSelectedText(selectedText);
    // Get the output box position
    const outputBox = outputBoxRef.current.getBoundingClientRect();
    
    setContextMenuPosition({ 
      x: e.clientX - outputBox.left + 10, // 10px offset from click
      y: e.clientY - outputBox.top + 10   // 10px offset from click
    });
    setShowContextMenu(true);
  }
};

  const handleRewriteOption = async (mode) => {
    if (!selectedText) return;
    
    setIsRewriting(true);
    setShowContextMenu(false);
    
    try {
      const modeMap = {
        'Strategic Clarity': 'strategic',
        'Plain Language Summary': 'plain',
        'Conversational Tone': 'conversational',
        'Executive Summary': 'executive',
        'Bullet Points': 'bullets',
        'Empathetic Reflection': 'empathetic',
        'Analogy or Metaphor': 'metaphor',
        'Reflective Questions': 'questions',
        'Counterpoint': 'counterpoint'
      };
      
      const backendMode = modeMap[mode] || 'plain';
      console.log(`Rewriting text with mode: ${backendMode}`, selectedText);
      
      const rewrittenText = await rewriteTextWithAI(selectedText, backendMode);
      
      if (rewrittenText) {
        console.log("Received rewritten text:", rewrittenText);
        
        const currentText = aiOutput;
        const startIndex = currentText.indexOf(selectedText);
        
        if (startIndex !== -1) {
          const newText = 
            currentText.substring(0, startIndex) + 
            rewrittenText + 
            currentText.substring(startIndex + selectedText.length);
          
          setAiOutput(newText);
          console.log("Text replaced successfully");
        } else {
          console.error("Could not find the selected text in the current output");
        }
      }
    } catch (error) {
      console.error("Failed to rewrite text:", error);
    } finally {
      setIsRewriting(false);
    }
  };

  // Don't render if not open
  if (!isOpen) return null;

  return (
    <div
      className="ai-composition-modal-container"
      style={{
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height
      }}
    >
      {/* Header/Drag Bar */}
      <div className="ai-composition-header" onMouseDown={handleMouseDown}>
        <h2>AI Composition</h2>
        <button className="ai-composition-close-btn" onClick={onClose}>
          <X size={20} />
        </button>
      </div>

      {/* Content */}
      <div className="ai-composition-body">
        {/* NEW: Prompt Generation Section - Add this BEFORE the existing sticky selection */}
<div className="ai-task-input-container">
  <label>Generate New Stickies from Prompt:</label>
  <textarea 
    value={promptForGeneration}
    onChange={(e) => setPromptForGeneration(e.target.value)}
    className="ai-task-input"
    placeholder="e.g., Create 3 characters for a fantasy novel with names, personalities, and backstories"
    style={{ height: '80px' }}
  />
  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px' }}>
    <label style={{ fontSize: '14px', margin: 0 }}>Number of stickies:</label>
    <input 
      type="number" 
      value={stickyCount}
      onChange={(e) => setStickyCount(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
      min="1" 
      max="10"
      style={{ 
        width: '60px', 
        padding: '4px 8px', 
        borderRadius: '4px', 
        border: '1px solid var(--stickyNoteBorder)' 
      }}
    />
  </div>
</div>

{/* Divider when both sections are available */}
{selectedStickies.length > 0 && promptForGeneration.trim() && (
  <div style={{ 
    textAlign: 'center', 
    margin: '20px 0', 
    color: 'var(--stickyNoteText)', 
    opacity: 0.7 
  }}>
    ── OR ──
  </div>
)}

{/* EXISTING: Sticky Selection Section (keep as-is) */}

        {selectedStickies.length > 0 ? (
          <>
            {/* Custom Task Input */}
            <div className={`ai-task-input-container ${aiOutput ? 'minimized' : ''}`}>
              <label>Customize AI Request:</label>
              <textarea 
                value={customTask}
                onChange={(e) => setCustomTask(e.target.value)}
                className="ai-task-input"
              />
            </div>

            {/* Sticky Notes Section */}
            <div className={`ai-composition-input-section ${aiOutput ? 'minimized' : ''}`}>
              <div className="ai-composition-instructions">
                <p>{aiOutput ? 'Based on these notes:' : 'The following pinned sticky notes will be used for AI composition:'}</p>
              </div>
              
              <div className="ai-composition-stickies-container">
                {selectedStickies.map((sticky, index) => formatStickyForDisplay(sticky, index))}
              </div>
            </div>

            {/* AI Output Section */}
            {aiOutput && (
              <div className="ai-composition-output">
                <h3>AI-Generated Content {isRewriting && '(Rewriting...)'}</h3>
                <div 
                  className="ai-output-box" 
                  ref={outputBoxRef}
                  onContextMenu={handleContextMenu}
                >
                  {aiOutput}
                </div>
              </div>
            )}

            {/* Context Menu */}
            {showContextMenu && (
              <div 
                className="context-menu"
                style={{
            position: 'absolute',
            top: `${contextMenuPosition.y + 200}px`,
            left: `${contextMenuPosition.x+ 200}px`,
          }}
              >
                <div className="context-menu-header">Rewrite As...</div>
                <div className="context-menu-item" onClick={() => handleRewriteOption('Strategic Clarity')}>
                  🧠 Strategic Clarity
                </div>
                <div className="context-menu-item" onClick={() => handleRewriteOption('Plain Language Summary')}>
                  💬 Plain Language Summary
                </div>
                <div className="context-menu-item" onClick={() => handleRewriteOption('Conversational Tone')}>
                  🗣️ Conversational Tone
                </div>
                <div className="context-menu-item" onClick={() => handleRewriteOption('Executive Summary')}>
                  📊 Executive Summary
                </div>
                <div className="context-menu-item" onClick={() => handleRewriteOption('Bullet Points')}>
                  📌 Bullet Points
                </div>
                <div className="context-menu-item" onClick={() => handleRewriteOption('Empathetic Reflection')}>
                  🔍 Empathetic Reflection
                </div>
                <div className="context-menu-item" onClick={() => handleRewriteOption('Analogy or Metaphor')}>
                  🧩 Analogy or Metaphor
                </div>
                <div className="context-menu-item" onClick={() => handleRewriteOption('Reflective Questions')}>
                  ❓ Reflective Questions
                </div>
                <div className="context-menu-item" onClick={() => handleRewriteOption('Counterpoint')}>
                  ⚖️ Counterpoint
                </div>
              </div>
            )}


          </>
        ) : (
          <div className="ai-composition-empty">
            <p>No sticky notes pinned. Please pin at least one sticky note to use AI Composition.</p>
          </div>
          
          
        )}
                {/* 🔥 ACTION BUTTONS - HERE, OUTSIDE THE CONDITIONAL */}
        <div className="ai-composition-actions">
          <button 
            className="ai-composition-send-btn"
            onClick={handleGenerateStickies}
            disabled={!promptForGeneration.trim() || isGeneratingFromHook || isCreatingSubnote || isRewriting}
          >
            {isGeneratingStickies ? "Generating..." : `Generate ${stickyCount} Stickies`}
          </button>
          
          {selectedStickies.length > 0 && (
            <button 
              className="ai-composition-send-btn"
              onClick={handleSendToAI}
              disabled={isLoading || isCreatingSubnote || isRewriting}
            >
              {isLoading ? "Processing..." : aiOutput ? "Regenerate" : "Send to AI"}
            </button>
          )}
          
          {aiOutput && (
            <button 
              className="ai-composition-create-subnote-btn"
              onClick={handleCreateSubnote}
              disabled={isSubnote || isCreatingSubnote || isRewriting}
              title={isSubnote ? "Cannot create a subnote from another subnote" : "Create a subnote with this content"}
            >
              {isCreatingSubnote ? "Creating..." : "Create Subnote"}
            </button>
          )}
          
          <button 
            className="ai-composition-close-btn"
            onClick={onClose}
            disabled={isCreatingSubnote || isRewriting}
          >
            Close
          </button>
        </div>

     
      </div>

      {/* Resize Handle */}
      <div 
        className="ai-composition-resize-handle" 
        onMouseDown={handleResizeStart}
      />
    </div>
  );
  
  
};

export default AICompositionModal;